import { getPublicClient } from '@wagmi/core'
import { getAddress } from 'viem'
import { config, pulseChain } from '@/config/wagmi'
import {
  CONTRACT_ADDRESSES,
  DIVINE_MANAGER_ABI,
  DIVINE_MANAGER_ADDRESSES,
  HOLY_C_ADDRESS,
  HOLYC_JIT_PAIR_ADDRESS,
  HOLYC_WPLS_PAIR_ADDRESS,
  JIT_ADDRESS,
  JIT_WPLS_PAIR_ADDRESS,
  WPLS_ADDRESS,
} from '@/config/contracts'
import { isDivineManagerV2, parseV2ProfitSettlement } from '@/lib/divineManagerV2'
import { getDivineManagerCatchupStartBlock } from '@/lib/divineManagerCatchupRange'
import type {
  DivineManagerExecution,
  DivineManagerStep,
  PoolKey,
  StepToken,
  StepType,
} from '@/hooks/useDivineManagerActivity'

const MAX_BLOCK_RANGE = 2_500n
const MIN_BLOCK_RANGE = 250n
const MAX_RETRIES = 3
const RETRY_DELAY_MS = 300
const TRANSFER_TOPIC = '0xddf252ad1be2c89b69c2b068fc378daa952ba7f163c4a11628f55a4df523b3ef'
const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000'

const HOLY_ADDRESS_LOWER = HOLY_C_ADDRESS.toLowerCase()
const JIT_ADDRESS_LOWER = JIT_ADDRESS.toLowerCase()
const WPLS_ADDRESS_LOWER = WPLS_ADDRESS.toLowerCase()
const BURN_ADDRESSES = new Set([ZERO_ADDRESS, CONTRACT_ADDRESSES.burn.toLowerCase()])

const PAIR_METADATA: Record<string, { key: PoolKey; label: string }> = {
  [HOLYC_WPLS_PAIR_ADDRESS.toLowerCase()]: { key: 'HOLYC_WPLS', label: 'HolyC/WPLS pool' },
  [JIT_WPLS_PAIR_ADDRESS.toLowerCase()]: { key: 'JIT_WPLS', label: 'JIT/WPLS pool' },
  [HOLYC_JIT_PAIR_ADDRESS.toLowerCase()]: { key: 'HOLYC_JIT', label: 'HolyC/JIT pool' },
}

type RawLog = {
  address: `0x${string}`
  topics: `0x${string}`[]
  data: `0x${string}`
}

type PulsePublicClient = NonNullable<ReturnType<typeof getPublicClient>>
type DivineManagerEventLog = Awaited<ReturnType<PulsePublicClient['getContractEvents']>>[number]

const sleep = (milliseconds: number) => new Promise<void>((resolve) => window.setTimeout(resolve, milliseconds))

const withRetry = async <T>(operation: () => Promise<T>): Promise<T> => {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await operation()
    } catch (error) {
      if (attempt >= MAX_RETRIES) throw error
      await sleep(RETRY_DELAY_MS * (attempt + 1))
    }
  }
}

const lowerAddress = (value: string) => {
  try {
    return getAddress(value).toLowerCase()
  } catch {
    return value.toLowerCase()
  }
}

const topicAddress = (topic?: `0x${string}`) => {
  if (!topic) return ZERO_ADDRESS
  try {
    return getAddress(`0x${topic.slice(-40)}`).toLowerCase()
  } catch {
    return ZERO_ADDRESS
  }
}

const parseTransfer = (log: RawLog) => {
  if (log.topics[0]?.toLowerCase() !== TRANSFER_TOPIC) return null
  return {
    from: topicAddress(log.topics[1]),
    to: topicAddress(log.topics[2]),
    value: log.data ? BigInt(log.data) : 0n,
  }
}

const tokenSymbol = (address: string): StepToken => {
  if (address === HOLY_ADDRESS_LOWER) return 'HOLYC'
  if (address === JIT_ADDRESS_LOWER) return 'JIT'
  if (address === WPLS_ADDRESS_LOWER) return 'WPLS'
  return 'UNKNOWN'
}

const createStep = (
  id: string,
  type: StepType,
  label: string,
  tokenInSymbol: StepToken,
  tokenOutSymbol: StepToken,
  tokenInAmount: bigint,
  tokenOutAmount = 0n,
  burns: { holyc: bigint; jit: bigint } = { holyc: 0n, jit: 0n },
  pool?: PoolKey
): DivineManagerStep => ({
  id,
  type,
  label,
  tokenInSymbol,
  tokenOutSymbol,
  tokenInAmount,
  tokenOutAmount,
  burns,
  ...(pool ? { pool } : {}),
})

const buildExecution = async (
  publicClient: PulsePublicClient,
  log: DivineManagerEventLog
): Promise<DivineManagerExecution | null> => {
  if (!log.transactionHash) return null
  const blockNumber = log.blockNumber ?? 0n
  const managerAddressLower = log.address.toLowerCase()
  let block: Awaited<ReturnType<PulsePublicClient['getBlock']>>
  let receipt: Awaited<ReturnType<PulsePublicClient['getTransactionReceipt']>>
  try {
    ;[block, receipt] = await Promise.all([
      withRetry(() => publicClient.getBlock({ blockNumber })),
      withRetry(() => publicClient.getTransactionReceipt({ hash: log.transactionHash! })),
    ])
  } catch {
    return null
  }

  let holyBurned = 0n
  let jitBurned = 0n
  let holyIn = 0n
  let holyOut = 0n
  let jitIn = 0n
  let jitOut = 0n
  let wplsIn = 0n
  let wplsOut = 0n
  const steps: DivineManagerStep[] = []
  const compileQueue: DivineManagerStep[] = []
  const restoreQueue: DivineManagerStep[] = []
  const completedRestores: DivineManagerStep[] = []
  const swapQueues = new Map<string, DivineManagerStep[]>()

  for (const receiptLog of receipt.logs ?? []) {
    const transfer = parseTransfer({
      address: receiptLog.address,
      topics: receiptLog.topics as `0x${string}`[],
      data: receiptLog.data,
    })
    if (!transfer) continue

    const tokenAddress = lowerAddress(receiptLog.address)
    const symbol = tokenSymbol(tokenAddress)
    const poolTo = PAIR_METADATA[transfer.to]
    const poolFrom = PAIR_METADATA[transfer.from]

    if (poolTo && transfer.from === managerAddressLower) {
      const step = createStep(
        `${log.transactionHash}-${steps.length + 1}-swap`,
        'swap',
        `${poolTo.label} swap`,
        symbol,
        'UNKNOWN',
        transfer.value,
        0n,
        { holyc: 0n, jit: 0n },
        poolTo.key
      )
      steps.push(step)
      if (!swapQueues.has(transfer.to)) swapQueues.set(transfer.to, [])
      swapQueues.get(transfer.to)!.push(step)
    }

    if (poolFrom && transfer.to === managerAddressLower) {
      const queue = swapQueues.get(transfer.from)
      const step = queue?.find((candidate) => candidate.tokenOutAmount === 0n) ?? queue?.shift()
      if (step) {
        step.tokenOutSymbol = symbol
        step.tokenOutAmount = transfer.value
      }
    }

    if (tokenAddress === HOLY_ADDRESS_LOWER) {
      if (transfer.from === managerAddressLower && BURN_ADDRESSES.has(transfer.to)) {
        holyBurned += transfer.value
        for (let index = completedRestores.length - 1; index >= 0; index -= 1) {
          const candidate = completedRestores[index]
          if (!candidate.isSettlement && candidate.tokenOutAmount === transfer.value) {
            candidate.isSettlement = true
            candidate.settlementAmount = transfer.value
            break
          }
        }
      }
      if (transfer.to === managerAddressLower) holyIn += transfer.value
      if (transfer.from === managerAddressLower) {
        holyOut += transfer.value
        if (transfer.to === JIT_ADDRESS_LOWER) {
          const step = createStep(
            `${log.transactionHash}-${steps.length + 1}-compile`,
            'compile',
            'Divine Compiler (HC→JIT)',
            'HOLYC',
            'JIT',
            transfer.value
          )
          steps.push(step)
          compileQueue.push(step)
        }
      }
      if (transfer.from === JIT_ADDRESS_LOWER && transfer.to === managerAddressLower) {
        const step = restoreQueue.find((candidate) => candidate.tokenOutAmount === 0n)
        if (step) {
          step.tokenOutAmount = transfer.value
          completedRestores.push(step)
        }
      }
    }

    if (tokenAddress === JIT_ADDRESS_LOWER) {
      if (BURN_ADDRESSES.has(transfer.to)) jitBurned += transfer.value
      if (transfer.to === managerAddressLower) {
        jitIn += transfer.value
        if (transfer.from === ZERO_ADDRESS) {
          const step = compileQueue.find((candidate) => candidate.tokenOutAmount === 0n)
          if (step) {
            step.tokenOutAmount = transfer.value
            const fee = step.tokenInAmount > transfer.value ? step.tokenInAmount - transfer.value : 0n
            if (fee > 0n) {
              step.burns.holyc += fee
              holyBurned += fee
            }
          }
        }
      }
      if (transfer.from === managerAddressLower) {
        jitOut += transfer.value
        if (transfer.to === ZERO_ADDRESS) {
          const step = createStep(
            `${log.transactionHash}-${steps.length + 1}-restore`,
            'restore',
            'Divine Compiler (JIT→HolyC)',
            'JIT',
            'HOLYC',
            transfer.value,
            0n,
            { holyc: 0n, jit: transfer.value }
          )
          steps.push(step)
          restoreQueue.push(step)
        }
      }
    }

    if (tokenAddress === WPLS_ADDRESS_LOWER) {
      if (transfer.to === managerAddressLower) wplsIn += transfer.value
      if (transfer.from === managerAddressLower) wplsOut += transfer.value
    }
  }

  if (holyBurned === 0n && jitBurned === 0n && holyIn === 0n && holyOut === 0n && jitIn === 0n && jitOut === 0n && wplsIn === 0n && wplsOut === 0n) {
    return null
  }

  return {
    source: 'divine-manager',
    managerAddress: log.address,
    transactionHash: log.transactionHash,
    blockNumber,
    timestamp: Number(block.timestamp) * 1000,
    strategyId: log.args?.strategyId ? String(log.args.strategyId) : '0x',
    jobNonce: log.args?.jobNonce ? String(log.args.jobNonce) : '0x',
    holyBurned,
    jitBurned,
    holyIn,
    holyOut,
    jitIn,
    jitOut,
    wplsIn,
    wplsOut,
    v2Settlement: isDivineManagerV2(log.address)
      ? parseV2ProfitSettlement(receipt.logs, log.address)
      : null,
    steps,
  }
}

export const fetchOnchainDivineManagerCatchup = async ({
  archivedThroughBlock,
  existingTransactionHashes,
}: {
  archivedThroughBlock: bigint
  existingTransactionHashes: ReadonlySet<string>
}): Promise<DivineManagerExecution[]> => {
  const publicClient = getPublicClient(config, { chainId: pulseChain.id })
  if (!publicClient) throw new Error('PulseChain public client is unavailable')

  const latestBlock = await withRetry(() => publicClient.getBlockNumber())
  let fromBlock = getDivineManagerCatchupStartBlock(archivedThroughBlock)
  if (fromBlock > latestBlock) return []

  let blockRange = MAX_BLOCK_RANGE
  const logsByTransaction = new Map<string, DivineManagerEventLog>()
  while (fromBlock <= latestBlock) {
    const proposedToBlock = fromBlock + blockRange - 1n
    const toBlock = proposedToBlock < latestBlock ? proposedToBlock : latestBlock
    try {
      const managerLogs = await withRetry(() => Promise.all(
        DIVINE_MANAGER_ADDRESSES.map((address) => publicClient.getContractEvents({
          address,
          abi: DIVINE_MANAGER_ABI,
          eventName: 'TicketExecuted',
          fromBlock,
          toBlock,
        }))
      ))
      for (const log of managerLogs.flat() as DivineManagerEventLog[]) {
        if (log.transactionHash) logsByTransaction.set(log.transactionHash.toLowerCase(), log)
      }
      fromBlock = toBlock + 1n
      if (blockRange < MAX_BLOCK_RANGE) {
        blockRange = blockRange * 2n > MAX_BLOCK_RANGE ? MAX_BLOCK_RANGE : blockRange * 2n
      }
    } catch (error) {
      if (blockRange <= MIN_BLOCK_RANGE) throw error
      blockRange = blockRange / 2n < MIN_BLOCK_RANGE ? MIN_BLOCK_RANGE : blockRange / 2n
    }
  }

  const freshLogs = [...logsByTransaction.entries()]
    .filter(([hash]) => !existingTransactionHashes.has(hash))
    .map(([, log]) => log)
  const executions: DivineManagerExecution[] = []
  for (let index = 0; index < freshLogs.length; index += 2) {
    const batch = await Promise.all(freshLogs.slice(index, index + 2).map((log) => buildExecution(publicClient, log)))
    executions.push(...batch.filter((execution): execution is DivineManagerExecution => execution !== null))
  }
  return executions
}
